import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import DataTable from '@/components/admin/DataTable';
import {
  ConfirmDeleteDialog,
  StatusBadge,
  courseLabel,
  labelize,
  programStatusOptions,
} from '@/components/admin/AcademicTableHelpers';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { createProgram, deleteProgram, getCourseRefs, getPrograms, updateProgram } from '@/lib/api';
import { Archive, Eye, GraduationCap, Plus, RotateCcw, Trash2 } from 'lucide-react';

const allOption = { value: 'all', label: 'All statuses' };
const emptyForm = {
  name: '',
  code: '',
  abbreviation: '',
  invoice_price: '',
  description: '',
  status: 'active',
  course_refs: [],
};

function filterParam(value) {
  return value === 'all' ? undefined : value;
}

function programDetail(detail) {
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return 'Program request failed';
}

function safeInvoicePrice(price) {
  if (price === '' || price == null) return null;
  const parsed = Number.parseFloat(price);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed * 100) / 100 : null;
}

function linkedCoursePreview(program, courseById) {
  const refs = Array.isArray(program.course_refs) ? program.course_refs : [];
  if (refs.length === 0) return 'No linked courses';
  return refs
    .slice(0, 3)
    .map((ref) => courseLabel(courseById.get(ref)) || ref)
    .join(', ');
}

function linkedCourseSearchText(program, courseById) {
  const refs = Array.isArray(program.course_refs) ? program.course_refs : [];
  return refs
    .map((ref) => courseLabel(courseById.get(ref)) || ref)
    .join(' ');
}

export default function ManagePrograms() {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteConflict, setDeleteConflict] = useState(null);
  const [archivingId, setArchivingId] = useState('');

  const courseById = useMemo(
    () => new Map(courses.map((course) => [course._id, course])),
    [courses],
  );

  const loadData = async () => {
    try {
      setLoading(true);
      const [programRes, courseRes] = await Promise.all([
        getPrograms({ status: filterParam(statusFilter) }),
        getCourseRefs(),
      ]);
      setPrograms(programRes.data || []);
      setCourses(courseRes.data || []);
    } catch (err) {
      console.error(err);
      toast.error(programDetail(err?.response?.data?.detail) || 'Could not load programs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const visiblePrograms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return programs;
    return programs.filter((program) => {
      const linked = linkedCourseSearchText(program, courseById);
      return [program.name, program.code, program.abbreviation, program.description, linked]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [courseById, programs, search]);

  const resetCreate = () => {
    setForm(emptyForm);
    setCreateOpen(true);
  };

  const handleCreate = async () => {
    const payload = {
      name: form.name.trim(),
      code: form.code.trim().toUpperCase(),
      abbreviation: form.abbreviation.trim().toUpperCase(),
      invoice_price: safeInvoicePrice(form.invoice_price),
      description: form.description.trim(),
      status: form.status,
      course_refs: [],
    };
    if (!payload.name || !payload.code || !payload.abbreviation) {
      toast.error('Program name, academic code, and abbreviation are required');
      return;
    }
    try {
      setSaving(true);
      const { data } = await createProgram(payload);
      toast.success('Program created');
      setCreateOpen(false);
      setForm(emptyForm);
      await loadData();
      navigate(`/admin/programs/${data.id}`);
    } catch (err) {
      console.error(err);
      toast.error(programDetail(err?.response?.data?.detail) || 'Could not create program');
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (program, status) => {
    try {
      setArchivingId(program.id);
      const { data } = await updateProgram(program.id, { status });
      setPrograms((prev) => prev.map((item) => (item.id === program.id ? data.program : item)));
      toast.success(`Program marked ${labelize(status).toLowerCase()}`);
      if (statusFilter !== 'all' && statusFilter !== status) {
        await loadData();
      }
    } catch (err) {
      console.error(err);
      toast.error(programDetail(err?.response?.data?.detail) || 'Could not update program status');
    } finally {
      setArchivingId('');
    }
  };

  const handleConfirmedDelete = async () => {
    if (!pendingDelete) return;
    try {
      setDeleting(true);
      await deleteProgram(pendingDelete.id);
      toast.success('Program deleted');
      setPendingDelete(null);
      await loadData();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409 && typeof detail === 'object') {
        setPendingDelete(null);
        setDeleteConflict({ program: pendingDelete, detail });
      } else {
        toast.error(programDetail(detail) || 'Could not delete program');
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleArchiveConflict = async () => {
    if (!deleteConflict?.program) return;
    await handleStatus(deleteConflict.program, 'archived');
    setDeleteConflict(null);
  };

  const columns = [
    {
      id: 'name',
      header: 'Program',
      sortable: true,
      sortAccessor: (program) => program.name || '',
      cellClassName: 'min-w-[280px]',
      cell: (program) => (
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#062B1A]/5">
            <GraduationCap className="h-4 w-4 text-[#062B1A]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-[#062B1A]">{program.name || 'Untitled program'}</p>
            <p className="line-clamp-1 text-xs text-[#062B1A]/45">{program.description || 'No description'}</p>
          </div>
        </div>
      ),
    },
    {
      id: 'code',
      header: 'Academic code',
      sortable: true,
      cellClassName: 'min-w-[110px]',
      cell: (program) => (
        <Badge variant="outline" className="border-[#D4AF37]/30 bg-[#D4AF37]/5 text-[#8A6508]">
          {program.code || '-'}
        </Badge>
      ),
    },
    {
      id: 'abbreviation',
      header: 'Abbreviation',
      sortable: true,
      cellClassName: 'min-w-[120px]',
      cell: (program) => (
        <Badge variant="outline" className="border-[#062B1A]/20 bg-[#062B1A]/5 font-semibold text-[#062B1A]">
          {String(program.abbreviation || '').trim().toUpperCase() || '-'}
        </Badge>
      ),
    },
    {
      id: 'status',
      header: 'Status',
      sortable: true,
      cellClassName: 'min-w-[120px]',
      cell: (program) => <StatusBadge value={program.status || 'active'} />,
    },
    {
      id: 'invoice_price',
      header: 'Invoice price',
      sortable: true,
      sortAccessor: (program) => Number(program.invoice_price || 0),
      cellClassName: 'min-w-[140px]',
      cell: (program) => (
        <span className="text-sm font-medium text-[#062B1A]">
          {program.invoice_price ? `$${Number(program.invoice_price).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
        </span>
      ),
    },
    {
      id: 'course_refs',
      header: 'Linked courses',
      sortable: true,
      sortAccessor: (program) => (program.course_refs || []).length,
      cellClassName: 'min-w-[260px]',
      cell: (program) => {
        const count = (program.course_refs || []).length;
        return (
          <div>
            <p className="text-sm font-medium text-[#062B1A]">{count} course{count === 1 ? '' : 's'}</p>
            <p className="line-clamp-1 text-xs text-[#062B1A]/45">{linkedCoursePreview(program, courseById)}</p>
          </div>
        );
      },
    },
  ];

  const rowActions = (program) => (
    <div className="flex justify-end gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-[#062B1A]/15 text-[#062B1A]"
        onClick={() => navigate(`/admin/programs/${program.id}`)}
      >
        <Eye className="mr-2 h-4 w-4" />
        Open
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={archivingId === program.id}
        className="border-[#D4AF37]/40 text-[#8A6508] hover:bg-[#D4AF37]/5"
        onClick={() => handleStatus(program, program.status === 'archived' ? 'active' : 'archived')}
      >
        {program.status === 'archived' ? <RotateCcw className="mr-2 h-4 w-4" /> : <Archive className="mr-2 h-4 w-4" />}
        {program.status === 'archived' ? 'Unarchive' : 'Archive'}
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
        onClick={() => setPendingDelete(program)}
      >
        <Trash2 className="mr-2 h-4 w-4" />
        Delete
      </Button>
    </div>
  );

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
            Manage Programs
          </h2>
          <p className="mt-1 text-sm text-[#062B1A]/60">
            {visiblePrograms.length} program{visiblePrograms.length === 1 ? '' : 's'} in the current view
          </p>
        </div>
        <Button className="bg-[#062B1A] text-white hover:bg-[#041B10] btn-hover" onClick={resetCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Program
        </Button>
      </div>

      <DataTable
        title="Program Management"
        description="Create programs, review archive state, and open details to manage linked courses."
        data={visiblePrograms}
        columns={columns}
        loading={loading}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search programs or linked courses..."
        filters={[{ id: 'status', value: statusFilter, onChange: setStatusFilter, options: [allOption, ...programStatusOptions] }]}
        rowActions={rowActions}
        onRowClick={(program) => navigate(`/admin/programs/${program.id}`)}
        emptyMessage="No programs match the current filters."
        pageSize={8}
      />

      <Dialog open={createOpen} onOpenChange={(open) => !saving && setCreateOpen(open)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-display)' }}>Add Program</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Name *</label>
              <Input
                value={form.name}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                placeholder="International Esports Management Diploma"
                className="border-[rgba(6,43,26,0.12)]"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Academic code *</label>
                <Input
                  value={form.code}
                  onChange={(event) => setForm((prev) => ({ ...prev, code: event.target.value.toUpperCase() }))}
                  placeholder="IEMD"
                  aria-label="Program academic code"
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Abbreviation *</label>
                <Input
                  value={form.abbreviation}
                  onChange={(event) => setForm((prev) => ({ ...prev, abbreviation: event.target.value.toUpperCase() }))}
                  placeholder="IEM"
                  aria-label="Program abbreviation"
                  maxLength={24}
                  spellCheck={false}
                  className="border-[rgba(6,43,26,0.12)]"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Status</label>
                <Select value={form.status} onValueChange={(value) => setForm((prev) => ({ ...prev, status: value }))}>
                  <SelectTrigger className="border-[rgba(6,43,26,0.12)]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {programStatusOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Invoice price (CAD)</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.invoice_price}
                onChange={(event) => setForm((prev) => ({ ...prev, invoice_price: event.target.value }))}
                placeholder="3900"
                className="border-[rgba(6,43,26,0.12)]"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-[#062B1A]/70">Description</label>
              <Textarea
                value={form.description}
                onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Short internal description"
                className="min-h-[110px] border-[rgba(6,43,26,0.12)]"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={saving}>Cancel</Button>
            <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F]" onClick={handleCreate} disabled={saving}>
              {saving ? 'Creating...' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDeleteDialog
        open={!!pendingDelete}
        onOpenChange={(open) => !open && setPendingDelete(null)}
        title={`Delete ${pendingDelete?.name || 'program'}?`}
        description="This permanently removes the program only when no students or agencies reference it. Archive is safer for active records."
        count={1}
        deleting={deleting}
        onConfirm={handleConfirmedDelete}
      />

      <AlertDialog open={!!deleteConflict} onOpenChange={(open) => !open && setDeleteConflict(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#062B1A]" style={{ fontFamily: 'var(--font-display)' }}>
              Archive this program instead?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {(deleteConflict?.detail?.students || 0)} students and {(deleteConflict?.detail?.agents || 0)} agencies reference this program. Archive it to hide it from active program pickers without breaking those records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!archivingId}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!!archivingId}
              onClick={handleArchiveConflict}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              Archive program
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
